import type { FC } from 'react';
import React, { useMemo } from 'react';
import { Subscribe } from 'unstated';
import { defineMessages } from 'react-intl-next';
import { styled } from '@compiled/react';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import type { ClassificationOption } from '@atlassian/data-classification-level';
import { EditPopup, ReadPopup } from '@atlassian/data-classification-level';

import {
	Attribution,
	ErrorDisplay,
	withTransparentErrorBoundary,
} from '@confluence/error-boundary';
import { FlagsStateContainer } from '@confluence/flags';

import { FlagStatus } from '../constants/FlagStatus';
import { SaveFlag } from '../SaveFlag';
import type { ContentClassificationMetadata } from '../hooks/useContentClassification';
import { getAllClassificationLevels } from '../utils/getAllClassificationLevels';
import { useDefaultAndSpaceClassificationLevels } from '../hooks/useDefaultAndSpaceClassificationLevels';
import type { ClassificationLevel } from '../ClassificationRadioOption';
import { useEditSubmitCallback } from '../hooks/useEditSubmitCallback';
import { useComputeDefaultClassificationDropdownOption } from '../hooks/useComputeDefaultClassificationDropdownOption';

import { ContentClassificationTag } from './ContentClassificationTag';

type EditorContentClassificationTagProps = {
	contentId: string;
	contentType: string;
	contentClassificationMetadata: ContentClassificationMetadata;
	isEditMode: boolean;
	customSitesTitleComponent?: boolean;
	spaceKey: string;
	isInEditorTitle?: boolean;
	isTitleCenterAligned?: boolean;
	isCustomSitesPageTitleFFOn?: boolean;
	userOffline?: boolean;
};

type TagWrapperProps = {
	isInEditorTitle: boolean;
	isTitleCenterAligned: boolean;
	isCustomSitesPageTitleFFOn: boolean;
	shouldChangeTagPlacement: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const TagWrapper = styled.div((props: TagWrapperProps) => ({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.075', '6px'),
	justifyContent:
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		props.isInEditorTitle && props.isTitleCenterAligned && props.isCustomSitesPageTitleFFOn
			? 'center'
			: 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	position: props.shouldChangeTagPlacement ? 'absolute' : 'initial',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	top: props.shouldChangeTagPlacement ? '-33px' : 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	left: props.shouldChangeTagPlacement ? '5px' : 'unset',
}));

const messages = defineMessages({
	errorTitle: {
		id: 'data-classification.content-classification.error-flag.title',
		defaultMessage: 'Update to classification failed to save',
		description: "Title for error flag shown when page's classification failed to save",
	},
	errorDescription: {
		id: 'data-classification.content-classification.error-flag.description',
		defaultMessage: 'Please try again.',
		description: "Secondary message for error flag shown when page's classification failed to save",
	},
});

// Responsible for all editor-specific classification functionality (i.e. function for rendering Select Classification popup component & rendering error flag)
const EditorContentClassificationTagComponent = ({
	contentId,
	contentType,
	contentClassificationMetadata,
	isEditMode,
	customSitesTitleComponent = false,
	spaceKey,
	isInEditorTitle = false,
	isTitleCenterAligned = false,
	isCustomSitesPageTitleFFOn = false,
	userOffline = false,
}: EditorContentClassificationTagProps) => {
	const {
		classification: currentClassification,
		spaceClassification,
		flagStatus,
		shouldDisplayClassification,
		spaceDefaultIsOverridden,
		getClassificationLevelFromId,
	} = contentClassificationMetadata;

	const spaceClassificationMetadata = useDefaultAndSpaceClassificationLevels(spaceKey);

	const { defaultClassificationOption, error: classificationOptionError } =
		useComputeDefaultClassificationDropdownOption({
			getClassificationLevelFromId,
			spaceDefaultClassificationLevel: spaceClassification,
		});

	const selectedOption = useMemo(() => {
		if (!spaceDefaultIsOverridden) {
			return defaultClassificationOption;
		} else {
			return currentClassification;
		}
	}, [currentClassification, spaceDefaultIsOverridden, defaultClassificationOption]);

	const allClassificationLevels: ClassificationLevel[] = !!spaceClassificationMetadata
		? [
				...getAllClassificationLevels(spaceClassificationMetadata?.classificationLevels),
				defaultClassificationOption,
			]
		: [];

	const handleEditSubmit = useEditSubmitCallback({
		contentId,
		contentClassificationMetadata,
	});
	const onEditSubmit = (classification: ClassificationOption, closePopup: () => void) => {
		handleEditSubmit(classification);
		closePopup();
	};

	if (!shouldDisplayClassification || !currentClassification) {
		return null;
	}

	const renderPopupComponent = (closePopup: () => void) =>
		isEditMode ? (
			<EditPopup
				options={allClassificationLevels}
				selectedOption={selectedOption || undefined}
				onEditCancel={closePopup}
				onEditSubmit={(classification: ClassificationOption) => {
					onEditSubmit(classification, closePopup);
				}}
				errorMessage={
					spaceClassificationMetadata?.expectedError
						? 'Unable to load classification. Try again.'
						: undefined
				}
				loading={flagStatus === FlagStatus.LOADING}
				isDisabled={userOffline}
			/>
		) : (
			<ReadPopup name={currentClassification.name} guideline={currentClassification.guideline} />
		);

	return (
		<TagWrapper
			isInEditorTitle={isInEditorTitle}
			isTitleCenterAligned={isTitleCenterAligned}
			isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
			shouldChangeTagPlacement={isInEditorTitle && !isCustomSitesPageTitleFFOn}
		>
			<ContentClassificationTag
				contentId={contentId}
				contentType={contentType || null}
				customSitesTitleComponent={customSitesTitleComponent}
				contentClassificationMetadata={contentClassificationMetadata}
				renderPopupComponent={userOffline ? undefined : renderPopupComponent}
				isEditMode
			/>
			{flagStatus === FlagStatus.LOADING && <Spinner size="small" />}
			{classificationOptionError ? <ErrorDisplay error={classificationOptionError} /> : null}
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) =>
					flagStatus === FlagStatus.ERROR && (
						<SaveFlag
							flags={flags}
							title={messages.errorTitle}
							description={messages.errorDescription}
							type="warning"
							showRefresh={false}
						/>
					)
				}
			</Subscribe>
		</TagWrapper>
	);
};

const EditorContentClassificationTagGetter: FC<EditorContentClassificationTagProps> = (props) => {
	return <EditorContentClassificationTagComponent {...props} />;
};

const EditorContentClassificationTagWithErrorBoundary = withTransparentErrorBoundary({
	attribution: Attribution.DLP,
})(EditorContentClassificationTagGetter);

export const EditorContentClassificationTag: FC<EditorContentClassificationTagProps> = (props) => (
	<EditorContentClassificationTagWithErrorBoundary {...props} />
);
